/**
 * React Query hooks for content package operations.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * @module hooks/useContentPackages
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { contentPackageService, ApiException } from '@/services';
import { showErrorToast } from '@/utils/errorToast';
import type { ContentPackageResponse } from '@/types';

/**
 * Query key factory for content packages.
 * Ensures consistent cache key generation.
 */
export const contentPackageKeys = {
  all: ['contentPackages'] as const,
  lists: () => [...contentPackageKeys.all, 'list'] as const,
  list: () => [...contentPackageKeys.lists()] as const,
  details: () => [...contentPackageKeys.all, 'detail'] as const,
  detail: (id: string) => [...contentPackageKeys.details(), id] as const,
};

/**
 * Hook to fetch all content packages
 *
 * @example
 * const { data, isLoading, error } = useContentPackages();
 */
export function useContentPackages() {
  console.log('[useContentPackages] Hook called');
  return useQuery({
    queryKey: contentPackageKeys.list(),
    queryFn: async () => {
      console.log('[useContentPackages] queryFn executing...');
      const result = await contentPackageService.list();
      console.log('[useContentPackages] queryFn completed, result:', result);
      return result;
    },
  });
}

/**
 * Hook to fetch a single content package by ID
 *
 * @param id - The content package ID
 * @example
 * const { data, isLoading, error } = useContentPackage('my-package-id');
 */
export function useContentPackage(id: string) {
  return useQuery({
    queryKey: contentPackageKeys.detail(id),
    queryFn: () => contentPackageService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a new content package
 *
 * @example
 * const { mutate, isPending } = useCreateContentPackage();
 * mutate({ file, labels }, { onSuccess: () => refetch() });
 */
export function useCreateContentPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, labels }: { file: File; labels?: Record<string, string> }) =>
      contentPackageService.create(file, labels),
    onSuccess: (data: ContentPackageResponse) => {
      // Invalidate and refetch content packages list
      queryClient.invalidateQueries({ queryKey: contentPackageKeys.lists() });
      // Pre-populate the detail cache
      queryClient.setQueryData(contentPackageKeys.detail(data.id), data);
      toast.success(`Content package "${data.id}" created successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create content package: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a content package
 *
 * @example
 * const { mutate, isPending } = useDeleteContentPackage();
 * mutate('my-package-id');
 */
export function useDeleteContentPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contentPackageService.delete(id),
    onSuccess: (_, id) => {
      // Invalidate the list cache
      queryClient.invalidateQueries({ queryKey: contentPackageKeys.lists() });
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: contentPackageKeys.detail(id) });
      toast.success(`Content package deleted successfully`);
    },
    onError: (error: Error) => {
      // Use detailed message from API if available
      const errorMessage =
        error instanceof ApiException && error.detail ? error.detail : error.message;
      showErrorToast(errorMessage, { duration: 6000 });
    },
  });
}
